import { useEffect, useRef, useState, useCallback } from "react";
import { Search, Sparkles, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";

interface GiphyItem {
  id: string;
  title: string;
  preview: string;
  editorStillUrl?: string;
  editorUrl?: string;
  url: string;
  width: number;
  height: number;
}

interface Props {
  onSelect: (gif: GiphyItem) => void;
}

const PAGE_SIZE = 24;

export function GiphyPickerPopover({ onSelect }: Props) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState<GiphyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchPage = useCallback(async (query: string, off: number, append: boolean) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/giphy-search?q=${encodeURIComponent(query)}&limit=${PAGE_SIZE}&offset=${off}`;
      const r = await fetch(url, {
        headers: {
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        cache: 'no-store',
      });
      const result = await r.json();

      if (result?.error) throw new Error(result.error);
      const newItems: GiphyItem[] = result?.items || [];
      setItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setHasMore(newItems.length === PAGE_SIZE);
    } catch (e: any) {
      setError(e?.message || "Erreur GIPHY");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      setOffset(0);
      setHasMore(true);
      fetchPage(q, 0, false);
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q, open, fetchPage]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (loadingMore || loading || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      const next = offset + PAGE_SIZE;
      setOffset(next);
      fetchPage(q, next, true);
    }
  };

  const Trigger = (
    <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => isMobile && setOpen(true)}>
      <Sparkles className="w-4 h-4" />
      GIF
    </Button>
  );

  const Content = (
    <>
      <div className="relative mb-2">
        <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher des GIFs..."
          className="pl-8"
          autoFocus={false}
        />
      </div>
      {loading && (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}
      {error && <p className="text-xs text-destructive p-2">{error}</p>}
      {!loading && (
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className={`grid grid-cols-3 gap-1 overflow-y-auto ${isMobile ? 'max-h-[60vh]' : 'max-h-[320px]'}`}
        >
          {items.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => {
                onSelect(g);
                setOpen(false);
              }}
              className="relative aspect-square overflow-hidden rounded border hover:ring-2 hover:ring-primary"
            >
              <img src={g.preview} alt={g.title} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
          {loadingMore && (
            <div className="col-span-3 flex justify-center py-2">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {!items.length && !error && (
            <p className="col-span-3 text-xs text-muted-foreground text-center py-6">Aucun résultat</p>
          )}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground text-center mt-2">Powered by GIPHY</p>
    </>
  );

  if (isMobile) {
    return (
      <>
        {Trigger}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-[95vw] w-[95vw] p-3 max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> GIFs
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden flex flex-col">{Content}</div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-1.5">
          <Sparkles className="w-4 h-4" />
          GIF
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[380px] p-2">
        {Content}
      </PopoverContent>
    </Popover>
  );
}
